import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import { API_URL } from "@/common/config";

function getFileName(response) {
  if (response.headers["content-disposition"] === undefined) return null;
  return response.headers["content-disposition"].split('"')[1];
}

const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = API_URL;
    const token = localStorage.getItem("token");
    if (token) {
      Vue.prototype.$http.defaults.headers.common["Authorization"] = token;
    }
  },

  query(resource, params) {
    return Vue.axios.get(resource, params).catch(error => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },

  get(resource, slug = "") {
    return Vue.axios.get(`${resource}/${slug}`).catch(error => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },

  edit(resource, id = "") {
    return Vue.axios.get(`${resource}/${id}/edit`).catch(error => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },

  post(resource, params) {
    return Vue.axios.post(`${resource}`, params);
  },

  upload(resource, file, params = {}) {
    let formData = new FormData();
    formData.append("file", file);
    for (const [key, value] of Object.entries(params)) {
      formData.append(key, value);
    }
    let headers = {
      "Content-Type": "multipart/form-data"
    };
    return Vue.axios.post(resource, formData, { headers: headers });
  },
  download(resource, fileName, params = {}) {
    return Vue.axios
      .put(resource, params, { responseType: "blob" })
      .then(response => {
        fileName = getFileName(response) || fileName;
        let fileURL = window.URL.createObjectURL(new Blob([response.data]));
        let fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute("download", fileName);
        document.body.appendChild(fileLink);
        fileLink.click();
      });
  },

  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  delete(resource) {
    return Vue.axios.delete(resource).catch(error => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  }
};

export default ApiService;

export const AuthService = {
  login(email, password) {
    return ApiService.post("users/sign_in", { user: { email, password } });
  },
  register(email, password) {
    return ApiService.post("users", { user: { email, password } });
  }
};

export const ReportsService = {
  get() {
    return ApiService.get("reports");
  },
  create() {
    return ApiService.post("reports");
  },
  show(id) {
    return ApiService.get(`reports/${id}`);
  },
  destroy(slug) {
    return ApiService.delete(`reports/${slug}`);
  },
  update(params) {
    return ApiService.update("reports", params.id, params);
  },
  download(id, params) {
    return ApiService.download(
      `reports/${id}/download`,
      "Final Report.pdf",
      params
    );
  },
  downloadPreview(id, params) {
    return ApiService.download(
      `reports/${id}/download_preview`,
      "Report Preview.pdf",
      params
    );
  }
};

export const PlanProvisionsService = {
  getOriginal(reportId) {
    return ApiService.edit("plan_provisions", reportId);
  },
  update(params) {
    return ApiService.update("plan_provisions", params.id, {
      plan_provision: params
    });
  },
  getAvailableSelectOptions() {
    return ApiService.get("plan_provisions/available_select_options");
  }
};

export const ArticleService = {
  get() {
    return ApiService.get("articles");
  }
};
export const ReportArticleService = {
  get(reportId) {
    return ApiService.get(`reports/${reportId}/articles`);
  },
  add(reportId, articleId) {
    return ApiService.post(`reports/${reportId}/articles`, { id: articleId });
  },
  destroy(reportId, articleId) {
    return ApiService.delete(`reports/${reportId}/articles/${articleId}`);
  }
};

export const ParticipantsService = {
  validate(file) {
    return ApiService.upload("reports/validate_participants_data", file);
  },
  save(reportId, file, includeTerminated) {
    return ApiService.upload(`reports/${reportId}/participants`, file, {
      include_terminated: includeTerminated
    });
  },
  downloadErrors(reportId) {
    return ApiService.download(
      `reports/${reportId}/participants/download_errors`,
      "Savings 1.csv"
    );
  },
  uploadFixed(reportId, file) {
    return ApiService.upload(
      `reports/${reportId}/participants/upload_fixed`,
      file
    );
  },
  update(reportId, id, params) {
    return ApiService.put(`reports/${reportId}/participants/${id}`, {
      participant: params
    });
  },
  getAnnualCostEstimation(reportId) {
    return ApiService.get(`reports/${reportId}/annual_cost`);
  },
  destroy(reportId, id) {
    return ApiService.delete(`reports/${reportId}/participants/${id}`);
  },
  invalidParticipants(reportId) {
    return ApiService.get(
      `reports/${reportId}/participants/invalid_participants`
    );
  },
  destroy_all(reportId, errorType) {
    return ApiService.delete(
      `reports/${reportId}/participants/destroy_all?error_type=${errorType}`
    );
  },
  ignore_all(reportId, errorType) {
    return ApiService.put(
      `reports/${reportId}/participants/ignore_all?error_type=${errorType}`
    );
  },
  calculate(reportId) {
    return ApiService.post(`reports/${reportId}/participants/calculate`);
  },
  compensationSegments(reportId) {
    return ApiService.get(
      `reports/${reportId}/participants/compensation_segments`
    );
  }
};

export const AlternativeDesignsService = {
  get(reportId) {
    return ApiService.get(`reports/${reportId}/alternative_designs`);
  },
  copy(reportId, id) {
    return ApiService.post(
      `reports/${reportId}/alternative_designs/${id}/copy`
    );
  },
  destroy(reportId, id) {
    return ApiService.delete(`reports/${reportId}/alternative_designs/${id}`);
  },
  update(reportId, id, params) {
    return ApiService.put(`reports/${reportId}/alternative_designs/${id}`, {
      plan_provision: params
    });
  },
  calculateSavingBehavior(reportId) {
    return ApiService.post(`reports/${reportId}/alternative_designs/calculate`);
  }
};

export const ComplianceLimitService = {
  get() {
    return ApiService.get("compliance_limits");
  }
};

export const CalculatorsService = {
  calcAnnualCost(params) {
    return ApiService.post("calculators/annual_costs", params);
  },
  calcDcBalances(params) {
    return ApiService.post("calculators/dc_balances", params);
  },
  calcDcContribution(params) {
    return ApiService.post("calculators/dc_contribution", params);
  },
  calcOther(params) {
    return ApiService.post("calculators/other", params);
  },
  calcOutputFields(params) {
    return ApiService.post("calculators/output_fields", params);
  },
  calcSocialSecurity(params) {
    return ApiService.post("calculators/social_security", params);
  },
  calcSocialSecurityTaxable(params) {
    return ApiService.post("calculators/social_security_taxable", params);
  },
  calcStateRetirementTaxes(params) {
    return ApiService.post("calculators/states_retirement_taxes", params);
  },
  calcStateTax(params) {
    return ApiService.post("calculators/state_tax", params);
  },
  calcContribution(params) {
    return ApiService.post("calculators/contribution", params);
  }
};
